import { withAuthenticationRequired } from "@auth0/auth0-react";
import dayjs, { type Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { USE_AUTH } from "../../api/apiConfig";
import useQueryClient from "../../api/query";
import LoadingSkeleton from "./LoadingSkeleton";
import MaintenanceCard from "./MaintenanceCard";
import { COLORS, INTERNAL_ORG_NAME } from "./styles";
import useMaintenancePolling from "./useMaintenancePolling";
import WieldyLogo from "./WieldyLogo";

dayjs.extend(utc);
dayjs.extend(timezone);

const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 5_000;

type PageState =
  | { status: "loading" }
  | {
      status: "ready";
      maintenanceType: MaintenanceType;
      start: Dayjs | undefined;
      end: Dayjs | undefined;
      isInternal: boolean;
    };

function MaintenancePage() {
  const [state, setState] = useState<PageState>({ status: "loading" });
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const applyOrg = useCallback(
    (org: OrganizationMessage) => {
      if (!org.inMaintenance) {
        navigate("/");
        return;
      }
      setState({
        status: "ready",
        maintenanceType: org.maintenanceType,
        start: org.maintenanceStart ? dayjs(org.maintenanceStart) : undefined,
        end: org.maintenanceEnd ? dayjs(org.maintenanceEnd) : undefined,
        isInternal: org.name === INTERNAL_ORG_NAME,
      });
    },
    [navigate],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding queryClient.getOrganization would change when this runs
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    let retryId: ReturnType<typeof setTimeout> | null = null;

    const load = async () => {
      attempts += 1;
      try {
        const org = await queryClient.getOrganization();
        if (cancelled) return;
        applyOrg(org);
      } catch {
        if (cancelled) return;
        if (attempts < MAX_RETRIES) {
          retryId = setTimeout(load, RETRY_DELAY_MS);
        }
        // after max retries, skeleton stays visible — safer than a blank or error screen
      }
    };

    load();

    return () => {
      cancelled = true;
      if (retryId !== null) clearTimeout(retryId);
    };
  }, [applyOrg]);

  useMaintenancePolling(state.status === "ready", applyOrg);

  if (state.status === "loading") {
    return <LoadingSkeleton />;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 40,
        background: COLORS.bg,
      }}
    >
      <WieldyLogo />
      <MaintenanceCard
        maintenanceType={state.maintenanceType}
        start={state.start}
        end={state.end}
        isInternal={state.isInternal}
      />
    </div>
  );
}

const AuthenticatedMaintenancePage = USE_AUTH
  ? withAuthenticationRequired(MaintenancePage, {
      onRedirecting: () => <LoadingSkeleton />,
    })
  : MaintenancePage;

export default AuthenticatedMaintenancePage;
